<template>
  <div class="api-customers-view">
    <page-header title="API Customers" />

    <section v-if="loaded">
      <empty-state
        v-if="customers.length === 0"
        icon="vpn_key"
        title="No API customers found"
      />

      <template v-else>
        <text-input
          v-model="query"
          class="filter"
          type="search"
          placeholder="Filter by name"
          remove-margin
        />

        <p class="volume-note">
          <template v-if="volumeUpdatedAt">
            Calls in the last 24 hours, cached for up to an hour. Updated {{ $filters.datetime(volumeUpdatedAt) }}.
          </template>
          <template v-else>
            Last 24 hours of call volume is unavailable.
          </template>
        </p>

        <p v-if="filteredCustomers.length === 0" class="no-matches">
          No customers match that name.
        </p>

        <div v-else class="list">
          <div class="list-header">
            <span>Customer</span>
            <span>24h calls</span>
          </div>

          <router-link
            v-for="customer in filteredCustomers"
            :key="customer.uid"
            class="customer-row"
            :to="`/api-customers/${customer.uid}`"
          >
            <span class="name">{{ customer.displayName }}</span>
            <span class="calls">{{ formatCalls(customer.calls24h) }}</span>
          </router-link>
        </div>
      </template>
    </section>

    <spinner v-else />
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';
import * as Sentry from '@sentry/vue';

export default {
  data() {
    return {
      loaded: false,
      customers: [],
      query: '',
      volumeUpdatedAt: null,
    };
  },

  created() {
    this.load();
  },

  computed: {
    filteredCustomers() {
      const query = this.query.trim().toLowerCase();
      const customers = query
        ? this.customers.filter((customer) => customer.displayName.toLowerCase().includes(query))
        : this.customers.slice();

      return customers.sort((a, b) => {
        const aCalls = a.calls24h == null ? -1 : a.calls24h;
        const bCalls = b.calls24h == null ? -1 : b.calls24h;

        if (bCalls !== aCalls) {
          return bCalls - aCalls;
        }

        return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
      });
    },
  },

  methods: {
    formatCalls(value) {
      if (value === null || value === undefined) {
        return '—';
      }

      return this.$filters.number(value);
    },

    async load() {
      this.loaded = false;

      try {
        const { data } = await firebase.functions().httpsCallable('httpListApiCustomers')();
        this.customers = (data && data.customers) || [];
        this.volumeUpdatedAt = (data && data.volumeUpdatedAt) || null;
      } catch (e) {
        this.$toasted.error(e.message || 'Error loading API customers');
        Sentry.captureException(e);
        this.customers = [];
        this.volumeUpdatedAt = null;
      }

      this.loaded = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.api-customers-view {
  @include container;
  min-height: 100%;
}

.filter {
  max-width: 280px;
}

.volume-note,
.no-matches {
  margin: $gp 0 0;
}

.volume-note {
  color: var(--theme-secondary-color, #666);
}

.list {
  margin-top: $gp * .5;
}

.list-header,
.customer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $gp;
}

.list-header {
  padding: $gp * .5 0;
  font-size: 12px;
  color: var(--theme-secondary-color, #666);
}

.customer-row {
  padding: $gp * .5 0;
  color: var(--theme-color);
  text-decoration: none;
  font-size: 14px;
  border-bottom: 1px solid var(--theme-border);

  &:hover {
    background-color: var(--theme-highlight);
  }
}

.name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calls {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
</style>
